import { useContext } from "react";

import Box from "@/components/Box";
import { CollapseableText } from "@/components/CollapseableText";
import DataLoader from "@/components/DataLoader";
import Text from "@/components/Text";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import Accordion from "@/tpo/Accordion";
import { ChartCardWrapper, RetestDueSubTitle } from "@/tpo/ChartCards";
import { ChartDetailModalLayout } from "@/tpo/Layouts";
import Stack from "@/tpo/Stack";
import { ViewerContext } from "@/tpo/Viewer";
import { dueForRetestOrExpired } from "@/utils/shared";

import DatapointCharts from "./DatapointCharts";
import DatapointGraph from "./DatapointGraph";
import RelatedResults from "./RelatedResults";

function UserDatapointTexts({ userDatapoint }) {
  return (
    <>
      {userDatapoint.datapoint?.description && (
        <Box>
          <Box as="h2" fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="130%">
            What is it?
          </Box>
          <Box py={[1, 1, 2]} />
          <CollapseableText fontSize={[14, 14, 16]}>
            {userDatapoint.datapoint?.description}
          </CollapseableText>
        </Box>
      )}
      {userDatapoint.datapoint?.reason && (
        <Box>
          <Box as="h2" fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="130%">
            Why measure it?
          </Box>
          <Box py={[1, 1, 2]} />
          <CollapseableText fontSize={[14, 14, 16]}>
            {userDatapoint.datapoint?.reason}
          </CollapseableText>
        </Box>
      )}
    </>
  );
}

function RelatedResultsAccordion({ query, userDatapoint, skip }) {
  const viewerContext = useContext(ViewerContext);
  const clientId = viewerContext?.userId;
  const orgContext = useOrganisationContext();
  const organisationId = orgContext?.organisation?.id;

  return (
    <Accordion.Item value="relatedResults">
      <Accordion.Control px={[15, 15, 20]} py={[2, 2, 15]}>
        <Text marginCorrection={false} fontFamily="gilroyBold" fontSize={[16, 16, 18]}>
          Related Results
        </Text>
      </Accordion.Control>
      <Accordion.Panel px={[20, 20, 40]} pb={[2, 2, 20]}>
        <DataLoader
          query={query}
          variables={{
            userDatapointId: userDatapoint.id,
            userId: clientId,
            organisationId
          }}
          fetchPolicy="no-cache"
          nextFetchPolicy="no-cache"
          skip={skip}
          render={({ userDatapoint }) => (
            <RelatedResults clientId={clientId} results={userDatapoint.results} py={[2, 2, 20]} />
          )}
        />
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export function UserDatapointModelContent({
  userDatapoint,
  datapointGraphQuery,
  datapointGraphSkip
}) {
  const isGenesAlleles = userDatapoint.datapoint.chartType === "GENES_ALLELES_CHART";

  return (
    <ChartDetailModalLayout
      chart={
        <Stack gap={[20, 20, 30]}>
          <ChartCardWrapper
            title={!isGenesAlleles ? userDatapoint.datapoint.name : null}
            subtitle={
              dueForRetestOrExpired(userDatapoint.dueForRetest, userDatapoint.dateExpired) &&
              !isGenesAlleles && <RetestDueSubTitle />
            }
            chart={<DatapointCharts datapoint={userDatapoint} />}
            showArrowCircle={false}
            pb={0}
            pt={0}
            showCotIcon={false}
          />
          {!isGenesAlleles && (
            <Box pt={[1, 1, 2]}>
              <DatapointGraph
                query={datapointGraphQuery}
                skip={datapointGraphSkip}
                userDatapoint={userDatapoint}
              />
            </Box>
          )}
        </Stack>
      }
      text={<UserDatapointTexts userDatapoint={userDatapoint} />}
    />
  );
}
